/**
 * React Query hooks for the Crypto Assets screen.
 *
 * Polling cadences:
 *   - Wallet overview: 2min (Monero node balance, balances aggregate — slow-moving)
 *   - Withdrawals: 1min (rows can transition states autonomously)
 *   - Per-row withdrawal status: 30s (only while a row is being inspected)
 *
 * The `useExchangeCurrencies` query is set to 5-min staleTime since the
 * supported currency list comes from the provider router and changes
 * rarely.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createExchange,
  ExchangeCreateInput,
  fetchAdminWithdrawals,
  fetchExchangeCurrencies,
  fetchExchangeEstimate,
  fetchPublisherBalances,
  fetchWalletOverview,
  fetchWithdrawalStatus,
  WithdrawalStatus
} from '@/api/adminWallet';

export function useWalletOverview() {
  return useQuery({
    queryKey: ['adminWalletOverview'],
    queryFn: fetchWalletOverview,
    refetchInterval: 120_000
  });
}

export function usePublisherBalances(params: {
  page?: number;
  limit?: number;
}) {
  return useQuery({
    queryKey: ['adminPublisherBalances', params],
    queryFn: () => fetchPublisherBalances(params),
    placeholderData: prev => prev
  });
}

export function useAdminWithdrawals(params: {
  page?: number;
  limit?: number;
  status?: WithdrawalStatus | '';
}) {
  return useQuery({
    queryKey: ['adminWithdrawals', params],
    queryFn: () => fetchAdminWithdrawals(params),
    refetchInterval: 60_000,
    placeholderData: prev => prev
  });
}

export function useWithdrawalStatus(id: string | null) {
  return useQuery({
    queryKey: ['adminWithdrawalStatus', id],
    queryFn: () => fetchWithdrawalStatus(id!),
    enabled: Boolean(id),
    refetchInterval: id ? 30_000 : false
  });
}

export function useExchangeCurrencies() {
  return useQuery({
    queryKey: ['adminCurrencies'],
    queryFn: fetchExchangeCurrencies,
    staleTime: 5 * 60 * 1000
  });
}

export function useExchangeEstimate(params: {
  from: string;
  to: string;
  amount: number;
}) {
  return useQuery({
    queryKey: ['adminEstimate', params],
    queryFn: () => fetchExchangeEstimate(params),
    enabled: Boolean(params.to) && params.amount > 0
  });
}

export function useCreateExchange() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ExchangeCreateInput) => createExchange(input),
    onSuccess: () => {
      // After a successful exchange the overview KPIs + withdrawal queue
      // both move, so invalidate both.
      qc.invalidateQueries({ queryKey: ['adminWalletOverview'] });
      qc.invalidateQueries({ queryKey: ['adminWithdrawals'] });
    }
  });
}
